import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, User, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/menu', label: 'Notre Carte' },
    { href: '/reviews', label: 'Avis' },
    { href: '/about', label: 'Notre Histoire' },
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'nav-glass' : 'bg-transparent'
      }`}
    >
      <nav className="container-padding max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="font-heading text-2xl font-bold text-navy">
              Racines <span className="text-terracotta">&</span> Épices <span className="text-navy">du Monde</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link ${
                  location.pathname === link.href ? 'text-terracotta' : ''
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-4">
            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2 text-navy hover:text-terracotta transition-colors"
              data-testid="cart-button"
            >
              <ShoppingBag size={24} />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-terracotta text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Auth */}
            {isAuthenticated ? (
              <div className="hidden md:flex items-center space-x-3">
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#3E2723] text-[#D4A373] hover:bg-[#5d3b34] transition-colors"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  className="flex items-center space-x-2 text-navy hover:text-terracotta transition-colors"
                  data-testid="account-button"
                >
                  <User size={20} />
                  <span className="font-medium">{user?.name?.split(' ')[0]}</span>
                </Link>
                <button
                  onClick={logout}
                  className="p-2 text-navy hover:text-terracotta transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut size={20} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:block btn-primary text-sm px-6 py-2"
                data-testid="login-button"
              >
                Connexion
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-navy"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-sand border-t border-navy/10"
          >
            <div className="container-padding py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block py-2 text-lg font-medium text-navy hover:text-terracotta"
                >
                  {link.label}
                </Link>
              ))}
              <hr className="border-navy/10" />
              {isAuthenticated ? (
                <>
                  <Link
                    to="/account"
                    className="flex items-center space-x-2 py-2 text-navy"
                  >
                    <User size={20} />
                    <span>Mon Compte</span>
                  </Link>
                  <button
                    onClick={logout}
                    className="flex items-center space-x-2 py-2 text-terracotta"
                  >
                    <LogOut size={20} />
                    <span>Déconnexion</span>
                  </button>
                </>
              ) : (
                <Link to="/login" className="block btn-primary text-center">
                  Connexion
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
